import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import LightboxModal from '../components/LightboxModal';
import { SERVICES_GRID } from '../data/scrapeData';

const HERO_IMAGES = [
  'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/97798019-8392-40d4-89ba-059b36d499c2/2026-04-24-11-34-48.jpg',
  'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/97798019-8392-40d4-89ba-059b36d499c2/2026-03-28-15-09-48-1.jpg',
  'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/97798019-8392-40d4-89ba-059b36d499c2/2026-03-03-09-57-45.jpg',
  'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/97798019-8392-40d4-89ba-059b36d499c2/2025-08-12-21-23-17.jpg'
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // 3-second autoplay slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % HERO_IMAGES.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Vognmand & Entreprenør DBM aps"
        description="Vognmand & Entreprenør DBM Aps - Din lokale Vognmand & Entreprenør. Vi udfører altid vores arbejde med præcision."
      />

      {/* Hero Banner Section with 3-sec Autoplay Slider */}
      <section className="relative min-h-[80vh] md:min-h-[85vh] flex items-center justify-center overflow-hidden bg-black text-white">
        {/* Background Image Slides */}
        {HERO_IMAGES.map((imgUrl, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 -z-10'
            }`}
            style={{ backgroundImage: `url('${imgUrl}')` }}
          />
        ))}

        {/* Dark overlay for optimal readability */}
        <div className="absolute inset-0 bg-black/40 z-10" />

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto text-center px-6 py-20 sm:py-28 flex flex-col items-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-6 drop-shadow-lg leading-tight">
            Vognmand & Entreprenør DBM Aps
          </h1>
          
          <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white/95 mb-10 drop-shadow-md">
            Din lokale Vognmand & Entreprenør.
          </p>

          {/* Primary CTA Button */}
          <a
            href="#mere"
            className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 font-semibold text-base sm:text-lg hover:bg-neutral-900 transition-all border border-white/20 shadow-xl"
          >
            <span>Læs mere</span>
            <i className="fa-solid fa-chevron-down text-sm" aria-hidden="true"></i>
          </a>
        </div>



        {/* Slider Indicator Dots */}
        <div className="absolute bottom-6 z-30 flex items-center space-x-3">
          {HERO_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Gå til slide ${index + 1}`}
              className={`h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-white' : 'w-3 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Intro Prose Section */}
      <section id="mere" className="py-16 sm:py-20 px-4 sm:px-8 max-w-4xl mx-auto text-center">
        <div className="space-y-6 text-gray-800 text-xl sm:text-2xl leading-relaxed font-normal tracking-wide">
          <p>
            Vi udfører altid vores arbejde med præcision, og vi gør alt hvad vi kan for vores kunder føler sig tilfredse, når vi har fuldført vores opgave. Vi står altid klar til at prøve på at hjælpe, og vi giver gerne råd/vejledning.
          </p>
          <p className="font-semibold text-gray-900 text-xl sm:text-2xl leading-relaxed">
            I er altid velkommen til at skrive/ringe ang nye opgaver, og vi vil give jer et uforpligtende tilbud i løbet af et par dage..
          </p>
        </div>
      </section>

      {/* Vores ydelser Section */}
      <section className="py-12 bg-white px-4 sm:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center text-gray-900 mb-10 uppercase tracking-tight">
            Vores ydelser
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-6 gap-6 sm:gap-8">
            {SERVICES_GRID.map((item, index) => {
              const total = SERVICES_GRID.length;
              const rem3 = total % 3;
              const rem2 = total % 2;

              let lgSpan = "lg:col-span-2";
              if (rem3 === 1 && index === total - 1) {
                lgSpan = "lg:col-span-6";
              } else if (rem3 === 2 && index >= total - 2) {
                lgSpan = "lg:col-span-3";
              }

              let mdSpan = "md:col-span-1";
              if (rem2 === 1 && index === total - 1) {
                mdSpan = "md:col-span-2";
              }

              let smSpan = "sm:col-span-1";
              if (rem2 === 1 && index === total - 1) {
                smSpan = "sm:col-span-2";
              }

              const cardInner = (
                <div className="bg-white rounded shadow-md overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow flex flex-col group h-full">
                  <div className="relative h-56 sm:h-60 md:h-64 overflow-hidden bg-gray-100">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-center bg-white text-center">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-black transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </div>
              );

              return item.href && item.href !== '#' ? (
                <Link key={index} to={item.href} className={`block h-full ${smSpan} ${mdSpan} ${lgSpan}`}>
                  {cardInner}
                </Link>
              ) : (
                <div key={index} className={`block h-full ${smSpan} ${mdSpan} ${lgSpan}`}>
                  {cardInner}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/kontakt/"
              className="inline-block bg-gray-900 hover:bg-black text-white font-bold px-10 py-4 rounded uppercase tracking-wider text-sm transition-colors shadow-lg"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        images={SERVICES_GRID.map(s => ({ src: s.image, title: s.title }))}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : SERVICES_GRID.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < SERVICES_GRID.length - 1 ? prev + 1 : 0))}
      />
    </div>
  );
}
